import React, { useEffect, useState, useMemo } from "react";
import { fetchBrandKeys, updateBrandStatus, createBrand } from "../api";
import { BrandRecord } from "../types";
import { Card } from "../components/Card";
import { CopyButton } from "../components/CopyButton";
import { DataTable, type ColumnDef } from "../components/DataTable";

interface BrandsKeyPageProps {
  registerState: (s: {
    loading: boolean;
    lastUpdated: Date | null;
    refetch: () => void;
  }) => void;
}

export const BrandsKeyPage: React.FC<BrandsKeyPageProps> = ({ registerState }) => {
  const [brands, setBrands] = useState<BrandRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toggling, setToggling] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // New Brand Form State
  const [newBrand, setNewBrand] = useState({
    brand_id: "",
    brand_name: "",
    api_key: "",
    rate_limit_per_min: 1000,
    rate_limit_per_day: 100000,
  });
  const [adding, setAdding] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await fetchBrandKeys();
      setBrands(data.brands);
      registerState({ loading: false, lastUpdated: new Date(), refetch: loadData });
    } catch (err: any) {
      setError(err.message || "Failed to load brands");
      registerState({ loading: false, lastUpdated: null, refetch: loadData });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (brand: BrandRecord) => {
    const nextStatus = brand.status === "ACTIVE" ? "DISABLED" : "ACTIVE";
    setToggling(brand._id);
    try {
      await updateBrandStatus(brand._id, nextStatus);
      setBrands(prev => prev.map(b => b._id === brand._id ? { ...b, status: nextStatus } : b));
    } catch (err: any) {
      alert(err.message || "Failed to update status");
    } finally {
      setToggling(null);
    }
  };

  const handleAddBrand = async (e: React.FormEvent) => {
    e.preventDefault();
    setAdding(true);
    try {
      await createBrand(newBrand);
      setNewBrand({
        brand_id: "",
        brand_name: "",
        api_key: "",
        rate_limit_per_min: 1000,
        rate_limit_per_day: 100000,
      });
      setShowAddForm(false);
      loadData();
    } catch (err: any) {
      alert(err.message || "Failed to create brand");
    } finally {
      setAdding(false);
    }
  };

  const filteredBrands = useMemo(() => {
    if (!searchTerm) return brands;
    const low = searchTerm.toLowerCase();
    return brands.filter(b => 
      b.brand_name.toLowerCase().includes(low) || 
      b.brand_id.toLowerCase().includes(low)
    );
  }, [brands, searchTerm]);

  const columns = useMemo<ColumnDef<BrandRecord>[]>(() => [
    {
      key: "brand",
      header: "Brand Identity",
      render: (r) => (
        <div className="flex flex-col gap-2">
          <span style={{ fontWeight: 700, fontSize: '13px' }}>{r.brand_name}</span>
          <span className="dim small" style={{ letterSpacing: '0.8px', fontSize: '10px' }}>{r.brand_id}</span>
        </div>
      )
    },
    {
      key: "api_key",
      header: "API Key",
      render: (r) => (
        <div className="flex items-center gap-8">
          <code className="dim small" style={{ background: 'var(--bg-3)', padding: '2px 6px', borderRadius: '4px' }}>
            {r.api_key.substring(0, 8)}...{r.api_key.substring(r.api_key.length - 8)}
          </code>
          <CopyButton text={r.api_key} />
        </div>
      )
    },
    {
      key: "limits",
      header: "Rate Limits (Min/Day)",
      align: "right",
      render: (r) => (
        <div className="flex flex-col items-end gap-2">
          <span style={{ color: 'var(--cyan)', fontWeight: 600, fontSize: '13px' }}>{r.rate_limit_per_min.toLocaleString()}</span>
          <span className="dim small" style={{ fontSize: '10px' }}>{r.rate_limit_per_day.toLocaleString()}</span>
        </div>
      )
    },
    {
      key: "apis",
      header: "Allowed APIs",
      render: (r) => (
        <div className="flex gap-4 flex-wrap">
          {r.allowed_apis.map(api => (
            <span key={api} className="tag tag-default" style={{ fontSize: '9px', padding: '1px 5px' }}>{api}</span>
          ))}
        </div>
      )
    },
    {
      key: "status",
      header: "Access Status",
      align: "center",
      render: (r) => (
        <div className="flex items-center justify-center gap-12">
          <span className={`status-badge ${r.status.toLowerCase()}`}>
            {r.status}
          </span>
          <button 
            className={`btn small ${r.status === "ACTIVE" ? "btn-danger" : "btn-primary"}`}
            onClick={() => handleToggleStatus(r)}
            disabled={toggling === r._id}
            style={{ fontSize: '10px', padding: '3px 10px', minWidth: '90px' }}
          >
            {toggling === r._id ? "..." : (r.status === "ACTIVE" ? "DEACTIVATE" : "ACTIVATE")}
          </button>
        </div>
      )
    }
  ], [toggling]);

  useEffect(() => {
    loadData();
  }, []);

  return (
    <div className="brands-key-page">
      <div className="page-header row justify-between items-center">
        <div>
          <h1>Brand Management</h1>
          <p>Configure API credentials and access status for all provisioned brands.</p>
        </div>
        <div className="row gap-12 items-center">
          <div className="search-wrap">
            <input 
              type="text" 
              className="input search-input" 
              placeholder="Search brands..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <span className="search-icon">🔍</span>
          </div>
          <button 
            className="btn btn-primary"
            onClick={() => setShowAddForm(!showAddForm)}
          >
            {showAddForm ? "CLOSE FORM" : "ADD NEW BRAND"}
          </button>
        </div>
      </div>

      {showAddForm && (
        <Card className="add-brand-card grid-margin" tone="info">
          <h3 style={{ margin: 0, fontSize: '14px', textTransform: 'uppercase', letterSpacing: '1px' }}>Onboard New Brand</h3>
          <form onSubmit={handleAddBrand} className="grid grid-2" style={{ gap: '24px 32px', marginTop: '20px' }}>
            <div className="field">
              <label>Brand Name</label>
              <input 
                className="input"
                placeholder="e.g. Fundo baba"
                value={newBrand.brand_name}
                onChange={e => setNewBrand({...newBrand, brand_name: e.target.value})}
                required
              />
            </div>
            <div className="field">
              <label>Brand ID (Unique)</label>
              <input 
                className="input"
                placeholder="FUNDO_BABA"
                value={newBrand.brand_id}
                onChange={e => setNewBrand({...newBrand, brand_id: e.target.value.toUpperCase().replace(/\s/g, '_')})}
                required
              />
            </div>
            <div className="field">
              <label>API Key (Optional)</label>
              <input 
                className="input"
                placeholder="Leave blank for random"
                value={newBrand.api_key}
                onChange={e => setNewBrand({...newBrand, api_key: e.target.value})}
              />
            </div>
            <div className="field">
              <label>Rate Limit (Requests per Minute)</label>
              <input 
                className="input"
                type="number"
                value={newBrand.rate_limit_per_min}
                onChange={e => setNewBrand({...newBrand, rate_limit_per_min: parseInt(e.target.value)})}
              />
            </div>
            <div className="field">
              <label>Rate Limit (Requests per Day)</label>
              <input 
                className="input"
                type="number"
                value={newBrand.rate_limit_per_day}
                onChange={e => setNewBrand({...newBrand, rate_limit_per_day: parseInt(e.target.value)})}
              />
            </div>
            <div className="field justify-end" style={{ alignSelf: 'flex-end' }}>
              <button className="btn btn-primary" type="submit" disabled={adding} style={{ height: '42px', padding: '0 32px' }}>
                {adding ? "CREATING..." : "CREATE BRAND"}
              </button>
            </div>
          </form>
        </Card>
      )}

      {error && <div className="error-card grid-margin">{error}</div>}

      <div className="grid-margin">
        <DataTable
          columns={columns}
          rows={filteredBrands}
          rowKey={(r) => r._id}
          loading={loading}
          emptyMessage={searchTerm ? `No brands matching "${searchTerm}"` : "No brands onboarded yet."}
        />
      </div>
    </div>
  );
};
